import { BEHAVIOUR_LABELS, bandDaysRemaining, readingMeaning, SNAPSHOT_DRILL_FOR_BEHAVIOUR, snapshotSeriesTrend, snapshotStatusWord, } from "@healthos/shared";
import { heartbeatCarrying, heartbeatCarryingSay } from "./heartbeat-watchlist.js";
/*
 * The Snapshot overview, as data. Which reading leads the week, which behaviour
 * is the focus, which rows are doors to a drill-in, and what each row says: all
 * of it is product knowledge that web and mobile must agree on, so it lives
 * here once and each client only renders it.
 */
/** The behaviours in the order the overview lists them, sleep first. */
export const SNAPSHOT_ROW_ORDER = [
    "sleep",
    "movement",
    "stress",
    "food",
    "connection",
];
/** What has to arrive before a behaviour reads, shown while it is quiet. */
export const SNAPSHOT_BEHAVIOUR_FEEDS = {
    sleep: "a night from a wearable, or one you log",
    food: "a logged meal",
    movement: "steps from a watch or phone",
    stress: "heart rate variability, or a mood check-in",
    connection: "a kudos or reaction in Community",
};
/** The one thing that would light a quiet behaviour, as a short invitation. */
export const SNAPSHOT_BEHAVIOUR_INVITES = {
    sleep: "Log sleep",
    food: "Log food",
    movement: "Add steps",
    stress: "Check in",
    connection: "Say hi",
};
export function snapshotStatus(score, ungraded) {
    if (score === null || ungraded)
        return { word: "Gathering", key: "gathering", tone: "quiet" };
    const word = snapshotStatusWord(score);
    const tone = word === "strong" || word === "steady"
        ? "calm"
        : word === "watch"
            ? "warm"
            : word === "quiet"
                ? "quiet"
                : "brand";
    return { word: word.charAt(0).toUpperCase() + word.slice(1), key: word, tone };
}
export function snapshotLeadReading(sharpest, drill, drills) {
    // The sharpest reading only leads where it belongs. Taken from any drill it
    // put sleep's efficiency under a stress habit and called it that habit's
    // trend, and led the week on whichever number sat furthest from its band
    // rather than on the behaviour actually being watched.
    if (sharpest?.display && sharpest.bandDisplay && (!drill || sharpest.drill === drill.id)) {
        return {
            label: sharpest.label,
            termKey: sharpest.key,
            display: sharpest.display,
            plural: sharpest.plural === true,
            bandDisplay: sharpest.bandDisplay,
            verdict: sharpest.verdict,
            inProgress: sharpest.inProgress === true,
            reason: "attention",
            // A drill's window belongs to its headline contributor, not to whichever
            // row turned out sharpest, so it dated efficiency or sitting with the
            // night its neighbour was measured on. Only the headline can be dated
            // from it; the rest go undated rather than wrongly dated.
            window: sharpest.key === drills?.[sharpest.drill]?.headline.key
                ? (drills?.[sharpest.drill]?.window ?? null)
                : null,
            target: null,
            formingDays: 0,
        };
    }
    const headline = drill?.headline;
    if (!headline?.display || !drill)
        return null;
    return {
        label: headline.label,
        termKey: headline.key,
        display: headline.display,
        plural: headline.plural === true,
        verdict: headline.verdict,
        inProgress: headline.inProgress === true,
        reason: "focus",
        bandDisplay: headline.bandDisplay,
        window: drill.window,
        target: drill.trend.target?.label ?? null,
        formingDays: bandDaysRemaining(drill.trend.series),
    };
}
/** The plain read of the lead, or null where the reading has no plain words yet. */
export function snapshotLeadMeaning(lead) {
    if (lead.inProgress)
        return null;
    return readingMeaning(lead.termKey, lead.verdict);
}
/** The hero's supporting line: what the lead reading was measured against. */
const LEAD_PLACE = {
    under: "Below",
    in: "Inside",
    over: "Above",
};
/**
 * The supporting line under the lead, with the number the member should read
 * first wrapped in `**` so each client can set it in its own emphasis.
 */
export function snapshotLeadSub(lead) {
    if (lead.bandDisplay) {
        const place = lead.verdict ? LEAD_PLACE[lead.verdict] : null;
        // A running total has nothing to sit above or below yet, and the band it
        // would be read against is a whole day's, so the line says whose day.
        if (lead.inProgress)
            return `A full day is usually **${lead.bandDisplay}**.`;
        return place
            ? `${place} your usual **${lead.bandDisplay}**.`
            : `Your usual is **${lead.bandDisplay}**.`;
    }
    if (lead.target)
        return `Your target is **${lead.target}**.`;
    if (lead.formingDays > 0) {
        const d = lead.formingDays;
        return `Your usual range forms after **${d} more ${d === 1 ? "day" : "days"}**.`;
    }
    return null;
}
export function snapshotTrendToMoveParts(lead) {
    if (!lead)
        return null;
    const verb = lead.plural ? "are" : "is";
    const rest = lead.inProgress
        ? ` so far today: ${lead.display}`
        : lead.bandDisplay
            ? ` ${verb} ${lead.display}, against your usual ${lead.bandDisplay}`
            : ` ${verb} ${lead.display}${lead.window ? ` ${lead.window.toLowerCase()}` : ""}`;
    return { label: lead.label, termKey: lead.termKey, rest };
}
/** "Snapshot · week of 8 September": the Monday of the week the snapshot was taken. */
export function snapshotWeekLabel(iso, locale) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return "Snapshot";
    const monday = new Date(d);
    monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    const day = monday.toLocaleDateString(locale, { day: "numeric", month: "long" });
    return `Snapshot · week of ${day}`;
}
/** Text split on `**` into runs, so a client without a markdown renderer can set emphasis. */
export function emphasisRuns(text) {
    return text
        .split("**")
        .map((part, i) => ({ text: part, strong: i % 2 === 1 }))
        .filter((run) => run.text.length > 0);
}
export function snapshotOverviewModel(payload, detail, goal) {
    const ids = SNAPSHOT_ROW_ORDER;
    const watching = ids.filter((b) => payload.scores[b].status === "watch");
    const quietIds = new Set(ids.filter((b) => payload.scores[b].status === "quiet"));
    const rankable = ids.filter((b) => !quietIds.has(b)).length >= 2;
    const locked = payload.state === "fresh" || quietIds.size === ids.length;
    const suppressed = new Map();
    for (const c of detail?.confounders ?? []) {
        for (const family of c.affects) {
            if (!suppressed.has(family))
                suppressed.set(family, c.label);
        }
    }
    const focus = ids
        .filter((b) => !quietIds.has(b) && !suppressed.has(b))
        .reduce((best, b) => best === null || payload.scores[b].score < payload.scores[best].score ? b : best, null);
    const levels = detail?.levels ?? [];
    const focusLevel = focus ? (levels.find((l) => l.behaviour === focus) ?? null) : null;
    const focusDrillId = focus ? SNAPSHOT_DRILL_FOR_BEHAVIOUR.get(focus) : undefined;
    const focusDrill = focusDrillId ? detail?.drills[focusDrillId] : undefined;
    const heroLead = snapshotLeadReading(detail?.sharpest ?? null, focusDrill, detail?.drills);
    const levelEvidence = focusLevel && focusDrill?.pillar === focusLevel.behaviour
        ? snapshotTrendToMoveParts(heroLead)
        : null;
    const baseline = detail?.baseline ?? null;
    const gathering = baseline !== null && baseline.day < baseline.need;
    // The findings still asking for something are what Heartbeat orders its options by, so a row can say how many it is holding rather than leaving the tie between the two surfaces to be inferred.
    const carrying = heartbeatCarrying(detail?.findings ?? [], detail?.watches);
    const rows = ids.map((b) => {
        const score = payload.scores[b];
        const suppressedBy = suppressed.get(b) ?? null;
        const quiet = score.status === "quiet" || suppressedBy !== null;
        return {
            behaviour: b,
            label: BEHAVIOUR_LABELS[b],
            score,
            drill: SNAPSHOT_DRILL_FOR_BEHAVIOUR.get(b) ?? null,
            focus: rankable && focus === b && suppressedBy === null,
            suppressedBy,
            goal: goal?.behaviour === b,
            level: levels.find((l) => l.behaviour === b)?.level ?? null,
            carrying: quiet ? null : heartbeatCarryingSay(carrying.get(b) ?? 0),
            carryingCount: quiet ? 0 : (carrying.get(b) ?? 0),
            quiet,
            value: suppressedBy ? "Not scored" : quiet ? "Not measured" : String(score.score),
            line: suppressedBy
                ? `${suppressedBy}. We are not scoring it this week.`
                : quiet
                    ? `Waiting for ${SNAPSHOT_BEHAVIOUR_FEEDS[b]}.`
                    : payload.rows[b].means,
        };
    });
    return {
        watching,
        quietIds,
        rankable,
        locked,
        suppressed,
        ungradedIds: new Set([...quietIds, ...suppressed.keys()]),
        focus,
        focusLevel,
        heroLead,
        levelEvidence,
        gathering,
        rows,
    };
}
export function snapshotDrillModel(payload, detail, drillId) {
    const drill = detail?.drills[drillId];
    if (!drill)
        return null;
    const pillar = drill.pillar;
    const entry = pillar && payload ? payload.scores[pillar] : null;
    const lead = snapshotLeadReading(detail?.sharpest?.drill === drill.id ? detail.sharpest : null, drill);
    return {
        drill,
        say: pillar && payload ? payload.rows[pillar].means : null,
        score: pillar && entry && entry.status !== "quiet"
            ? { value: entry.score, behaviour: pillar }
            : null,
        evidence: snapshotTrendToMoveParts(lead),
        lead,
        level: pillar ? (detail?.levels.find((l) => l.behaviour === pillar) ?? null) : null,
    };
}
/**
 * What the big number is, in the member's terms: their own target where they
 * have set one, otherwise the reading and the window it was taken over. A drill
 * that grades nothing says so rather than implying a bar it does not have.
 */
export function snapshotDrillDescribe(drill) {
    if (drill.pillar === null)
        return "not scored";
    if (drill.trend.target)
        return `${drill.trend.target.label} target`;
    return `${drill.headline.label}, ${drill.window.toLowerCase()}`;
}
/** The Snapshot digest on Today, as data. */
export function snapshotDigestProps(payload, detail, goal) {
    const model = snapshotOverviewModel(payload, detail, goal);
    const rows = model.rows.map((row) => {
        const { word, key, tone } = snapshotStatus(row.quiet ? null : row.score.score, row.quiet);
        return {
            behaviour: row.behaviour,
            label: row.label,
            // The reading, always.
            line: row.line,
            // The row's own value when there is no score, so the chip and the value column on Snapshot say the same thing about the same row:
            status: row.quiet ? row.value : word,
            standing: key,
            // The direction of the line the row draws, not of its score band. The
            // band is the word; a mark shaped like an arrow has to mean the arrow.
            trend: snapshotSeriesTrend(payload.rows[row.behaviour].chart ?? []),
            tone,
            spark: payload.rows[row.behaviour].chart ?? [],
            gathering: row.quiet,
            level: row.level,
            goal: row.goal,
            drill: row.drill,
        };
    });
    // Named once, at the foot: the behaviours the member's own scores and their
    // goal are pointing at. A member with nothing graded has no focus to name.
    const focus = model.rows
        .filter((row) => !row.quiet && (row.focus || row.goal || row.score.status === "watch"))
        .map((row) => row.label);
    return { headline: payload.headline, say: digestSay(model), rows, focus };
}
/** The line under the headline. */
function digestSay(model) {
    // The focus, not the first watch row in row order:
    const watch = model.rows.find((row) => !row.quiet && row.behaviour === model.focus && row.score.status === "watch");
    if (!watch)
        return null;
    const carrying = heartbeatCarryingSay(watch.carryingCount);
    return carrying
        ? `${watch.label} scores lowest this week, and ${carrying}. Nothing here needs an answer from you.`
        : `${watch.label} scores lowest this week. Nothing here needs an answer from you.`;
}
//# sourceMappingURL=snapshot-overview.js.map